import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiUserPlus, FiUpload, FiSearch, FiTrash2, FiUsers, FiList } from "react-icons/fi";
import { HiOutlineCheckCircle, HiOutlineXCircle } from "react-icons/hi";
import ListSelectorBar from "./ListSelectorBar";
import AddContactModal from "./AddContactModal";
import ImportContactsModal from "./ImportContactsModal";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { getColorFromText } from "../styles";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

export default function ContactsTab({ data }) {
  const {
    contacts,
    lists,
    createList,
    deleteList,
    addContact,
    removeContact,
    removeContacts,
    importContacts,
  } = data;
  const [activeListId, setActiveListId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [removeSelectedOpen, setRemoveSelectedOpen] = useState(false);
  const [deleteListTarget, setDeleteListTarget] = useState(null);
  const [alsoRemoveListContacts, setAlsoRemoveListContacts] = useState(false);

  const listNameById = useMemo(
    () => Object.fromEntries(lists.map((l) => [l.id, l.name])),
    [lists]
  );

  const totalContacts = contacts.length;
  const activeContacts = contacts.filter((c) => c.status === "active").length;
  const removedContacts = contacts.filter((c) => c.status === "removed").length;

  const filtered = contacts.filter((c) => {
    const matchSearch =
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchList = !activeListId || c.listIds.includes(activeListId);
    return matchSearch && matchList;
  });

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
  const someFilteredSelected = filtered.some((c) => selectedIds.has(c.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtered.forEach((c) => {
        if (allFilteredSelected) next.delete(c.id);
        else next.add(c.id);
      });
      return next;
    });
  };

  const toggleSelect = (contactId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  // Ao trocar o filtro de lista, limpa a seleção — evita remover contatos de
  // outra lista por engano (a seleção não fica visível na tela).
  const handleSelectList = (listId) => {
    setActiveListId(listId);
    setSelectedIds(new Set());
  };

  const handleRemoveConfirmed = async () => {
    if (!removeTarget) return;
    try {
      await removeContact(removeTarget.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(removeTarget.id);
        return next;
      });
      toast.success("Contato removido");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao remover contato");
    } finally {
      setRemoveTarget(null);
    }
  };

  const handleRemoveSelectedConfirmed = async () => {
    const ids = Array.from(selectedIds);
    try {
      const result = await removeContacts(ids);
      setSelectedIds(new Set());
      toast.success(`${result?.removed ?? ids.length} contato(s) removido(s)`);
      if (result?.failures > 0) {
        toast.warn(`${result.failures} contato(s) falharam ao remover`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao remover contatos");
    } finally {
      setRemoveSelectedOpen(false);
    }
  };

  // Contatos ativos cuja ÚNICA lista é a que será excluída — só esses podem
  // ser removidos junto com a lista (os demais são só desanexados, pois
  // podem pertencer a outras listas válidas).
  const exclusiveContactsCount = useMemo(() => {
    if (!deleteListTarget) return 0;
    return contacts.filter(
      (c) =>
        c.status === "active" &&
        (c.listIds || []).length === 1 &&
        c.listIds[0] === deleteListTarget.id
    ).length;
  }, [contacts, deleteListTarget]);

  const handleDeleteListConfirmed = async () => {
    // Captura antes do await: o ConfirmDialog chama onClose() (que zera o
    // state) logo após onConfirm, sem esperar a promise resolver.
    const target = deleteListTarget;
    if (!target) return;
    try {
      await deleteList(target.id, { removeContacts: alsoRemoveListContacts });
      toast.success(`Lista "${target.name}" excluída`);
      if (activeListId === target.id) setActiveListId(null);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao excluir lista");
    } finally {
      setDeleteListTarget(null);
      setAlsoRemoveListContacts(false);
    }
  };

  const handleCreateList = async (name) => {
    try {
      await createList(name);
      toast.success(`Lista "${name}" criada`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao criar lista");
    }
  };

  return (
    <>
      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><FiUsers /></div>
            <div className="text">
              <p>{totalContacts}</p>
              <p>Total de Contatos</p>
            </div>
          </div>
        </div>
        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb"><FiList /></div>
            <div className="text">
              <p>{lists.length}</p>
              <p>Listas Criadas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><HiOutlineCheckCircle /></div>
            <div className="text">
              <p>{activeContacts}</p>
              <p>Ativos / Inscritos</p>
            </div>
          </div>
        </div>
        <div className="kpi-card danger">
          <div className="card-content">
            <div className="thumb"><HiOutlineXCircle /></div>
            <div className="text">
              <p>{removedContacts}</p>
              <p>Removidos / Inválidos</p>
            </div>
          </div>
        </div>
      </div>

      <ListSelectorBar
        lists={lists}
        activeListId={activeListId}
        onSelect={handleSelectList}
        onCreateList={handleCreateList}
        onDeleteList={setDeleteListTarget}
      />

      <div className="toolbar">
        <div className="toolbar-left">
          <button className="btn-new" onClick={() => setAddModalOpen(true)}>
            <FiUserPlus /> Adicionar Contato
          </button>
          <button className="btn-secondary" onClick={() => setImportModalOpen(true)}>
            <FiUpload /> Importar Planilha
          </button>
          {selectedIds.size > 0 && (
            <button className="btn-danger-outline" onClick={() => setRemoveSelectedOpen(true)}>
              <FiTrash2 /> Remover selecionados ({selectedIds.size})
            </button>
          )}
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="main-content">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><FiUsers /></div>
            <h4>
              {searchTerm || activeListId ? "Nenhum contato encontrado" : "Nenhum contato cadastrado"}
            </h4>
            <p>
              {searchTerm || activeListId
                ? "Ajuste os filtros ou o termo de busca"
                : "Adicione contatos manualmente ou importe uma planilha"}
            </p>
            {!searchTerm && !activeListId && (
              <button className="empty-cta" onClick={() => setAddModalOpen(true)}>
                <FiUserPlus /> Adicionar Contato
              </button>
            )}
          </div>
        ) : (
          <div className="contacts-table">
            <div className="table-header">
              <input
                type="checkbox"
                title="Selecionar todos os contatos exibidos"
                checked={allFilteredSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
                }}
                onChange={toggleSelectAll}
              />
              <span>Nome</span>
              <span>Email</span>
              <span>Listas</span>
              <span>Status</span>
              <span>Adicionado em</span>
              <span />
            </div>
            <div className="table-body">
              {filtered.map((contact) => (
                <div className="table-row" key={contact.id}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(contact.id)}
                    onChange={() => toggleSelect(contact.id)}
                  />
                  <span className="cell-name">{contact.name}</span>
                  <span className="cell-email">{contact.email}</span>
                  <div className="cell-tags">
                    {contact.listIds.map((lid) => (
                      <span
                        key={lid}
                        className="tag"
                        style={{ background: getColorFromText(listNameById[lid]) }}
                      >
                        {listNameById[lid] || "—"}
                      </span>
                    ))}
                  </div>
                  <span className={`cell-status ${contact.status}`}>
                    {contact.status === "active" ? "Ativo" : "Removido"}
                  </span>
                  <span className="cell-date">{formatDate(contact.createdAt)}</span>
                  <div className="cell-actions">
                    <button title="Remover contato" onClick={() => setRemoveTarget(contact)}>
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AddContactModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        lists={lists}
        onAddContact={addContact}
      />

      <ImportContactsModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        lists={lists}
        onImport={importContacts}
      />

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirmed}
        title="Remover contato"
        message={`Deseja remover ${removeTarget?.name || "este contato"} da lista de emails?`}
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
      />

      <ConfirmDialog
        isOpen={removeSelectedOpen}
        onClose={() => setRemoveSelectedOpen(false)}
        onConfirm={handleRemoveSelectedConfirmed}
        title="Remover contatos"
        message={`Deseja remover os ${selectedIds.size} contato(s) selecionado(s)? Eles ficarão com status "Removido" e não receberão novas campanhas.`}
        confirmText={`Remover ${selectedIds.size} contato(s)`}
        cancelText="Cancelar"
        type="danger"
      />

      <ConfirmDialog
        isOpen={!!deleteListTarget}
        onClose={() => {
          setDeleteListTarget(null);
          setAlsoRemoveListContacts(false);
        }}
        onConfirm={handleDeleteListConfirmed}
        title="Excluir lista"
        message={`Deseja excluir a lista "${deleteListTarget?.name}"? Os contatos serão desanexados dela, mas continuarão cadastrados (a menos que a opção abaixo seja marcada).`}
        confirmText="Excluir lista"
        cancelText="Cancelar"
        type="danger"
      >
        {exclusiveContactsCount > 0 && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
              fontSize: 14,
              lineHeight: 1.4,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={alsoRemoveListContacts}
              onChange={(e) => setAlsoRemoveListContacts(e.target.checked)}
            />
            Remover também os {exclusiveContactsCount} contato(s) que estão somente nesta lista
          </label>
        )}
      </ConfirmDialog>
    </>
  );
}
